import { z, object, number, string, boolean, enum as zodEnum, nativeEnum } from 'zod';
import { DeleteMemberAction } from '../types';

const configUrlValidatorDescription = 'Must be a valid Config URL';
const configUrlValidator = (url = '') => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const wsUrlValidatorDescription = 'Must be a valid WebSocket URL';
const wsUrlValidator = (url = '') => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'ws:' || parsed.protocol === 'wss:';
  } catch {
    return false;
  }
};

const booleanFromString = (defaultValue = false) => {
  return z.preprocess((value) => {
    if (value === 'true') {
      return true;
    } else if (value === 'false') {
      return false;
    }
    return value;
  }, boolean().default(defaultValue));
};

export const envSchema = object({
  REACT_APP_DEPLOYMENT_ENV: string(),
  REACT_APP_OC_RELEASE: string().optional(),
  REACT_APP_OC_STAGE: zodEnum([
    'coco',
    'cefco',
    'delek',
    'mpc',
    'gulf',
    'yesway',
    'raceway',
    'gkraceway',
    'egamerica',
    'rofo',
    'stinker',
    'jacksons',
    'refuel',
    'parkers',
  ]).default('coco'),
  REACT_APP_BRAND_NAME: string(),
  REACT_APP_EXPERIENCE_URL: string()
    .optional()
    .refine((value) => !value || configUrlValidator(value), {
      message: configUrlValidatorDescription,
    }),
  REACT_APP_IS_LITE_VERSION: booleanFromString(),
  REACT_APP_INTEGRATION_GUIDE_URL: string().default('about:blank'),

  REACT_APP_OC_SENTRY_DSN: string().optional(),
  REACT_APP_ACTIVITY_LOG_POLLING_INTERVAL: number().default(10000),

  REACT_APP_COMMAND_API_KEY: string(),
  REACT_APP_COMMAND_API_URL: string().refine(configUrlValidator, {
    message: configUrlValidatorDescription,
  }),
  REACT_APP_COMMAND_API_WS_URL: string()
    .optional()
    .refine((value) => !value || wsUrlValidator(value), {
      message: wsUrlValidatorDescription,
    }),

  REACT_APP_API_HOST: string(),
  REACT_APP_API_DOC_URL: string().default('about:blank'),

  REACT_APP_OC_GOOGLE_MAPS_API_KEY: string().optional(),
  REACT_APP_GOOGLE_ANALYTICS_ID: string().optional(),

  REACT_APP_CONFIG_COMMUNICATION_PREFERENCE_ENABLED: booleanFromString(),
  REACT_APP_CONFIG_COMMUNICATION_PREFERENCE_SMS_OPT_IN_CONFIGURABLE: booleanFromString(),
  REACT_APP_CONFIG_COMMUNICATION_PREFERENCE_EMAIL_OPT_IN_CONFIGURABLE: booleanFromString(),
  REACT_APP_CONFIG_COMMUNICATION_PREFERENCE_PUSH_OPT_IN_CONFIGURABLE: booleanFromString(),

  REACT_APP_SITE_MANAGEMENT_SCAN_DATA_TAB_ENABLED: booleanFromString(),
  REACT_APP_PROMPT_AT_PUMP_ENABLED: booleanFromString(),
  REACT_APP_CONFIG_LOCATION_DETAILS_MPA_SIMULATOR_ENABLED: booleanFromString(true),
  REACT_APP_EXTERNAL_BANNERS_ENABLED: booleanFromString(),
  REACT_APP_PROMOCODES_OFFERS_ENABLED: booleanFromString(),

  REACT_APP_WALLET_STEERING_ENABLED: booleanFromString(),

  REACT_APP_RETAILER_CONNECT_ENABLED: booleanFromString(),
  REACT_APP_RETAILER_CONNECT_NETWORK_MANAGEMENT_ENABLED: booleanFromString(true),
  REACT_APP_RETAILER_CONNECT_NETWORK_ADD_EXISTING_USER_ENABLED: booleanFromString(true),

  REACT_APP_MOBILE_TRANSACTIONS_ENABLED: booleanFromString(false),

  REACT_APP_DELETE_MEMBER_ENABLED: booleanFromString(false),
  REACT_APP_DELETE_MEMBER_ACTION: nativeEnum(DeleteMemberAction).default(
    DeleteMemberAction.SCRUB_DATA,
  ),
  REACT_APP_MESSAGING_CENTER_ENABLED: booleanFromString(false),
  REACT_APP_STORED_VALUE_ENABLED: booleanFromString(false),
});

export type EnvConfig = z.infer<typeof envSchema>;
